/**
 * Keyword URLs for the service pages.
 *
 * The provider's codes ("tg", "mm", "ig") were the whole URL, and nobody searches for
 * "tg" — they search for "telegram". The slug is derived from the service name, so
 * /en/sms/telegram is the address people can read in a result and a link.
 *
 * `slugify` is mirrored byte-for-byte in the backend sitemap generator
 * (`_service_slug` in main.py). Both derive from the same service names, so the sitemap
 * lists exactly the URLs this router resolves. Change one, change the other.
 */
import { SERVICE_NAMES } from "@/lib/hero_services";

export function slugify(name: string): string {
  return (
    name
      .trim()
      .toLowerCase()
      // Names pack alternatives in ("Google,youtube,Gmail", "Instagram+Threads",
      // "TikTok/Douyin"): the first one is the one people search for.
      .split(/[,+/]/)[0]
      .normalize("NFD")
      .replace(/[\u0300-\u036f]/g, "")
      .replace(/[^a-z0-9]+/g, "-")
      .replace(/^-+|-+$/g, "")
  );
}

function buildMaps() {
  const codeToSlug: Record<string, string> = {};
  const slugToCode: Record<string, string> = {};

  // Sorted so the winner of a collision is stable between builds rather than depending
  // on object key order.
  for (const code of Object.keys(SERVICE_NAMES).sort()) {
    const base = slugify(SERVICE_NAMES[code]);
    if (!base) continue;
    // Distinct services do share a name ("Any other" appears more than once). First one
    // keeps the clean slug; the rest carry their code so every page stays reachable.
    const slug = slugToCode[base] === undefined ? base : `${base}-${code}`;
    if (slugToCode[slug] !== undefined) continue;
    slugToCode[slug] = code;
    codeToSlug[code] = slug;
  }
  return { codeToSlug, slugToCode };
}

const { codeToSlug, slugToCode } = buildMaps();

/** The public URL segment for a service code. Falls back to the code itself. */
export function serviceSlug(code: string): string {
  return codeToSlug[code] ?? code;
}

/**
 * Resolve whatever is in the URL to a service code: a slug, or a bare code for the old
 * links and bookmarks. Null when it is neither.
 */
export function serviceCodeFromParam(param: string): string | null {
  const key = param.toLowerCase();
  if (slugToCode[key] !== undefined) return slugToCode[key];
  if (SERVICE_NAMES[param] !== undefined) return param;
  return null;
}

/** True when the URL used the provider code and should be sent to the readable one. */
export function isLegacyCodeUrl(param: string): boolean {
  return (
    slugToCode[param.toLowerCase()] === undefined &&
    SERVICE_NAMES[param] !== undefined &&
    serviceSlug(param) !== param
  );
}
